import { useEffect, useRef, useState } from 'react'
import { Clock, X } from 'lucide-react'
import { ActivityScope } from '../lib/activity'
import { ActivityFeed } from './ActivityFeed'

/**
 * Clock-icon button that reveals a recent-activity panel for the current page
 * (last 10 events + "See more"). Drop into a page header with the page's scope.
 */
export function ActivityClock({ scope, label = 'Recent activity' }: { scope: ActivityScope; label?: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Close on outside-click and Escape.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title={label}
        aria-label={label}
        className={`flex items-center justify-center h-10 w-10 rounded-lg border transition-colors ${
          open
            ? 'border-brand-400 bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300 dark:border-brand-700'
            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-0'
        }`}
      >
        <Clock className="h-5 w-5" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-100 shadow-xl z-40 animate-fadeIn">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-brand-500" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{label}</h3>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-surface-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[26rem] overflow-y-auto px-4 py-3">
            <ActivityFeed scope={scope} emptyHint="Nothing has happened here yet." />
          </div>
        </div>
      )}
    </div>
  )
}
